import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApprovalTier, ChainStep, ReplaceApprovalTiers } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined, docType: string) =>
  ['approval-matrix', companyId, docType] as const;

export function useApprovalMatrix(companyId: string | undefined, docType: string) {
  return useQuery({
    queryKey: key(companyId, docType),
    queryFn: () =>
      api<ApprovalTier[]>(`/companies/${companyId}/approval-matrix?docType=${docType}`),
    enabled: !!companyId && !!docType,
  });
}

export function useReplaceApprovalTiers(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ReplaceApprovalTiers) =>
      apiJson<ReplaceApprovalTiers, ApprovalTier[]>(
        'PUT',
        `/companies/${companyId}/approval-matrix`,
        data,
      ),
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: key(companyId, vars.docType) }),
  });
}

// Optional branch/cost-center conditions let an admin preview the chain that
// actually resolves for a scoped tier (the backend `preview` endpoint accepts
// branchId/departmentId; resolveChain became condition-aware). Omitting both
// keeps the amount-only behaviour (company-wide tiers).
export function useApprovalPreview(
  companyId: string | undefined,
  docType: string,
  amount: string,
  conditions: { branchId?: string | undefined; departmentId?: string | undefined } = {},
) {
  const { branchId, departmentId } = conditions;
  return useQuery({
    queryKey: ['approval-preview', companyId, docType, amount, branchId ?? '', departmentId ?? ''],
    queryFn: () => {
      const qs = new URLSearchParams({ docType, amount });
      if (branchId) qs.set('branchId', branchId);
      if (departmentId) qs.set('departmentId', departmentId);
      return api<ChainStep[]>(`/companies/${companyId}/approval-matrix/preview?${qs.toString()}`);
    },
    enabled: !!companyId && !!docType && amount !== '',
  });
}
